import { gql, useQuery } from '@apollo/client';
import { Exception } from '@livecontrol/core-ui';
import { Obj } from '@livecontrol/core-utils';
import { useEffect, useState } from 'react';
import type { QueryResult } from '../graphql';
import type { EnterpriseAssociation, EnterpriseGroup } from './types';

export const QUERY = gql`
  query GetEnterpriseGroup($groupId: Int!) {
    getEnterpriseGroup(groupId: $groupId) {
      group_name
      associated_cids
      enterprise_associations {
        id
        login_enabled
        user {
          id
          first_name
          last_name
          organization_name
          last_event {
            id
            name
            start_time
            end_time
          }
          upcoming_event {
            id
            name
            start_time
            end_time
          }
          last_month_event_count
          past_event_count
          upcoming_event_count
          locations {
            id
            name
            physical_state
            physical_city
            secondary_id
            is_simulated_live
            is_disabled
          }
        }
      }
    }
  }
`;

interface TVariables {
  groupId?: number;

}

interface TData {
  getEnterpriseGroup: {
    id: number;
    group_name: string;
    associated_cids: number[];
    enterprise_associations: {
      id: number;
      login_enabled: boolean;
      user: {
        id: number;
        email: string;
        first_name: string;
        last_name: string;
        organization_name: string;
        last_event: {
          id: number;
          name: string;
          start_time: string;
          end_time: string;
        };
        upcoming_event: {
          id: number;
          name: string;
          start_time: string;
          end_time: string;
        };
        last_month_event_count: number;
        past_event_count: number;
        upcoming_event_count: number;
        locations: {
          id: string;
          secondary_id?: string;
          name: string;
          physical_state: string;
          physical_city: string;
          is_simulated_live: boolean;
          is_disabled?: boolean;
        }[];
      };
    }[];
  };
}

interface Args {
  groupId: number;
}

export const useGetEnterpriseGroup = (
  args: Args
): QueryResult<EnterpriseGroup | undefined, 'group'> => {
  const [error, setError] = useState<Error | undefined>();
  const [group, setGroup] = useState<EnterpriseGroup | undefined>();

  /* eslint-disable react-hooks/rules-of-hooks */

  const variables = { groupId: args.groupId  };

  // Validate the input
  if (!Obj.isHydrated(variables)) {
    return { loading: false, error: Exception.KABOOM };
  }

  // Prepare the query
  const { loading, data } = useQuery<TData, TVariables>(QUERY, {
    variables,
    onError() {
      setError(Exception.KABOOM);
    }
  });

  // When available, parse server response
  useEffect(() => {
    let error_;

    if (data) {
      const element = data.getEnterpriseGroup;

      const associations: EnterpriseAssociation[] = element.enterprise_associations.map((association) => {
          const { user } = association;

          return {
            id: association.id,
            login_enabled: association.login_enabled,
            user: {
              id: Number(user.id),
              email: user.email,
              first_name: user.first_name,
              last_name: user.last_name,
              organization_name: user.organization_name,
              last_event: user.last_event,
              upcoming_event: user.upcoming_event,
              last_month_event_count: user.last_month_event_count,
              past_event_count: user.past_event_count,
              upcoming_event_count: user.upcoming_event_count,
              locations: user.locations
            }
          };
        });

        const group_: EnterpriseGroup = {
          id: element.id,
          group_name: element.group_name,
          associated_cids: element.associated_cids,
          enterprise_associations: associations
        };

        setGroup(group_);
    }

    setError(error_);
  }, [data]);

  return { loading, error, group };
};
